import React, { useCallback, useMemo, useState } from 'react';
import { RefreshControl } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Scroll, Stack } from '@/components/layout';
import { Loading } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import GuardianScreenHeader from '@/features/guardian-player/components/guardian-screen-header';
import { useCoGuardianProfile } from '@/features/guardian-player/hooks/use-co-guardian-profile';
import type { CoGuardianMutualKidVM } from '@/features/guardian-player/types';
import { formatGuardianApiDate } from '@/features/guardian-player/utils';
import type { GuardianPlayer } from '@/features/guardian/services/get-guardian-players';
import { createStyles, useScaling } from '@/hooks';
import { guardianColors } from '@/theme/colors';

import MutualKidDetailSheet from './mutual-kid-detail-sheet';
import MutualKidsSection from './mutual-kids-section';
import ProfileIdentity from './profile-identity';
import RecentActivitiesSection from './recent-activities-section';
import RemoveCoGuardianButton from './remove-co-guardian-button';

const strings = STRINGS.guardianPlayer.coGuardianProfile;

export type CoGuardianProfileContentProps = {
  player: GuardianPlayer;
  /** Target co-guardian's stable id; drives both profile + activities requests. */
  guardianUid?: string;
  /** Phone-only: back navigation from the standalone screen. Ignored on tablet. */
  onBack?: () => void;
  /** Whether the signed-in guardian owns this player's circle and can remove co-guardians. */
  isPrimaryGuardian: boolean;
  /** Remove-from-circle action. Only rendered for the signed-in circle owner. */
  onRemove: () => void;
};

/**
 * Co-Guardian Profile — identity, mutual kids, player-scoped recent activities (BR-01) and
 * Remove. Renders as a standalone screen on phone and as the Guardian Circle right pane on
 * tablet (BR-03), where the surrounding screen already owns the header.
 */
export default function CoGuardianProfileContent({
  player,
  guardianUid,
  onBack,
  isPrimaryGuardian,
  onRemove,
}: CoGuardianProfileContentProps) {
  const styles = useStyles();
  const { scale, isTablet } = useScaling();
  const insets = useSafeAreaInsets();

  const { profile, activities, isLoading, isActivitiesLoading, isRefetching, refetch } =
    useCoGuardianProfile(player.id, guardianUid);

  const [selectedKid, setSelectedKid] = useState<CoGuardianMutualKidVM | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const handlePressKid = useCallback((kid: CoGuardianMutualKidVM) => {
    setSelectedKid(kid);
    setSheetOpen(true);
  }, []);

  const handleRefresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const refreshControl = useMemo(
    () => <RefreshControl refreshing={isRefetching} onRefresh={handleRefresh} />,
    [isRefetching, handleRefresh],
  );

  const firstName = player.firstname?.trim() || strings.fallbackPlayerName;
  const screenTitle = strings.screenTitle.replace('{name}', firstName);

  // The circle owner (role "owner") is the primary guardian.
  const isOwner = profile?.role?.toLowerCase() === 'owner';

  // For the circle owner's profile, show when the player's profile was created (from the
  // player's `createdOn`) in place of the "Joined since" line.
  const createdProfileLabel = useMemo(() => {
    if (!isOwner) return undefined;
    const date = formatGuardianApiDate(player.createdOn, 'MMM yyyy');
    return date ? strings.createdProfileSince.replace('{date}', date) : undefined;
  }, [isOwner, player.createdOn]);

  const body = profile ? (
    <>
      <ProfileIdentity profile={profile} createdProfileLabel={createdProfileLabel} />
      <MutualKidsSection kids={profile.mutualKids} onPressKid={handlePressKid} />
      {isTablet && (
        <Stack flex={1} height={1} minHeight={1} backgroundColor={guardianColors['4']} />
      )}
      <RecentActivitiesSection items={activities} isLoading={isActivitiesLoading} />
    </>
  ) : null;

  // Only the signed-in circle owner can remove a co-guardian.
  const removeButton = isPrimaryGuardian ? (
    <RemoveCoGuardianButton label={strings.remove} onPress={onRemove} />
  ) : null;

  const detailSheet = (
    <MutualKidDetailSheet open={sheetOpen} kid={selectedKid} onOpenChange={setSheetOpen} />
  );

  // ─── Tablet: right pane of the Guardian Circle (header owned by the circle screen). ───
  if (isTablet) {
    if (isLoading) {
      return (
        <Stack flex={1} align="center" justify="center">
          <Loading />
        </Stack>
      );
    }
    return (
      <Stack flex={1}>
        <Scroll
          style={styles.tabletScroll}
          showsVerticalScrollIndicator={false}
          refreshControl={refreshControl}
          contentContainerStyle={styles.tabletContent}>
          {body}
        </Scroll>
        {removeButton ? (
          <Stack paddingX={scale(24)} paddingBottom={scale(16)}>
            {removeButton}
          </Stack>
        ) : null}
        {detailSheet}
      </Stack>
    );
  }

  // ─── Phone: standalone screen. ───
  return (
    <Stack
      viewType="gradient"
      colors={['#EEF3FF', guardianColors['1'], '#EEF3FF']}
      locations={[0.08, 0.47, 0.91]}
      start={{ x: 1, y: 0 }}
      end={{ x: 0, y: 1 }}
      style={styles.mobileRoot}>
      <GuardianScreenHeader
        title={screenTitle}
        onBack={onBack ?? (() => undefined)}
        titleAlign="center"
        containerProps={{ backgroundColor: 'transparent' }}
      />

      {isLoading ? (
        <Stack flex={1} align="center" justify="center">
          <Loading />
        </Stack>
      ) : (
        <Scroll
          style={styles.mobileScroll}
          showsVerticalScrollIndicator={false}
          refreshControl={refreshControl}
          contentContainerStyle={[styles.mobileContent, { paddingBottom: scale(20) }]}>
          {body}
        </Scroll>
      )}

      {removeButton ? (
        <Stack paddingX={scale(16)} style={{ paddingBottom: insets.bottom + scale(16) }}>
          {removeButton}
        </Stack>
      ) : null}

      {detailSheet}
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  mobileRoot: {
    flex: 1,
  },
  mobileScroll: {
    flex: 1,
  },
  mobileContent: {
    paddingHorizontal: scale(16),
    gap: scale(20),
  },
  tabletScroll: {
    flex: 1,
  },
  tabletContent: {
    paddingHorizontal: scale(24),
    paddingTop: scale(16),
    paddingBottom: scale(20),
    gap: scale(20),
  },
}));
